import React, { useState, ChangeEvent, useEffect } from "react";
import { Form, Button, ToggleButton, ToggleButtonGroup, FloatingLabel, Row, Col } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import styles from "./NewEditRecipeForm.module.css";
import { Api } from "../../../services/Api";
import Select from "react-select";
import { MultiValue } from "react-select";
import CreatableSelect from "react-select/creatable";
import { selectStyles, selectTheme } from "../../../utils/selectUtils/selectUtils";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { setToast } from "../../../redux/actions/appActions";

export interface IRecipeNewEdit {
    id?: number;
    userId?: number;
    name: string;
    sourceUrl: string;
    imageUrl: string;
    preparationTime: string;
    isPublic: boolean;
    ingredients: string;
    description: string;
    languageId: string;
    languageName: string;
    portionCount: string | undefined;
}

interface INewRecipeForm {
    recipe: IRecipeNewEdit | null;
    isEditMode: boolean;
}

type ValueLabelOption = { value: string; label: string };

const NewEditRecipeForm: React.FC<INewRecipeForm> = (props) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const isMobile = useSelector((state: RootState) => state.client.isMobile);
    const navigate = useNavigate();
    const [isLoadingLanguages, setIsLoadingLanguages] = useState(true);
    const [isLoadingAllTags, setIsLoadingAllTags] = useState(true);
    const [isLoadingSelectedTags, setIsLoadingSelectedTags] = useState(true);
    const [languages, setLangauges] = useState<ValueLabelOption[]>([]);
    const [tags, setTags] = useState<ValueLabelOption[]>([]);
    const [nameError, setNameError] = useState("");
    const [languageError, setLanguageError] = useState("");
    const [ingredientsError, setIngredientsError] = useState("");
    const [descriptionError, setDescriptionError] = useState("");
    const [sourceUrlError, setSourceUrlError] = useState("");
    const [imageError, setImageError] = useState("");
    const [preparationTimeError, setPreparationTimeError] = useState("");
    const [selectStyle, setSelectStyle] = useState({});
    const [selectedTags, setSelectedTags] = useState<ValueLabelOption[]>([]);
    const [uploadedImage, setUploadedImage] = useState<any>(null);
    const [isCopyrightConfirmed, setIsCopyrightConfirmed] = useState(false);
    const [recipe, setRecipe] = useState<IRecipeNewEdit>({
        name: "",
        sourceUrl: "",
        imageUrl: "",
        preparationTime: "",
        isPublic: true,
        ingredients: "",
        description: "",
        languageId: "0",
        languageName: t("components.feature.newEditRecipeForm.language"),
        portionCount: undefined,
    });
    const [selectedLanguage, setSelectedLanguage] = useState({
        value: recipe.languageId,
        label: recipe.languageName,
    });
    const id = useParams().id;

    const getOrUpdateTags = () => {
        Api.get("tags/all").then((result) => {
            setTags(
                result.data.map((tag: any) => ({
                    value: tag.id,
                    label: tag.name,
                })),
            );
            setIsLoadingAllTags(false);
        });
    };

    const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setRecipe({ ...recipe, [name]: value });
    };

    const handleIsPublicToggle = (value: boolean) => {
        setRecipe({ ...recipe, isPublic: value });
    };

    const handleSelectChange = (event: any) => {
        const { label, value } = event;
        setSelectedLanguage({ value, label });
        setRecipe({
            ...recipe,
            languageName: label,
            languageId: value,
        });
    };

    const handleTagsChange = (newValue: MultiValue<ValueLabelOption>) => {
        setSelectedTags(newValue as ValueLabelOption[]);
    };

    const handleImageError = () => {
        setImageError("Image is invalid");
    };

    const onPhotoUpload = (event: any) => {
        const file = event.target.files?.[0];
        if (file) {
            resizeImage(file);
        } else {
            setUploadedImage(null);
        }
    };

    const onCopyrightConfirmed = (event: ChangeEvent<HTMLInputElement>) => {
        const isChecked: boolean = event.target.checked;
        setIsCopyrightConfirmed(isChecked);
    };

    const resizeImage = (file: File) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            const image = new Image();
            image.src = reader.result as string;
            image.onload = () => {
                const maxWidth = 1080;
                const maxHeight = 1080;
                let width = image.width;
                let height = image.height;

                if (width > maxWidth || height > maxHeight) {
                    const ratio = Math.min(maxWidth / width, maxHeight / height);
                    width *= ratio;
                    height *= ratio;
                }

                const canvas = document.createElement("canvas");
                canvas.width = width;
                canvas.height = height;

                const ctx = canvas.getContext("2d");
                ctx?.drawImage(image, 0, 0, width, height);

                canvas.toBlob((blob) => {
                    if (blob) {
                        const resizedImage = new File([blob], file.name, {
                            type: file.type,
                            lastModified: Date.now(),
                        });
                        setUploadedImage(resizedImage);
                    }
                }, file.type);
            };
        };
    };

    const handleCreateTag = (tagName: string) => {
        setIsLoadingAllTags(true);

        var bodyJson = {
            Name: tagName,
        };

        Api.post("tags/add", bodyJson).then((id) => {
            setTags((prev) => [...prev, { value: id, label: tagName }]);
            setSelectedTags((prev) => [...prev, { value: id, label: tagName }]);
            getOrUpdateTags();
        });
    };

    const isNameValid = () => {
        if (recipe.name.length === 0) {
            setNameError("Name must have at least one character");
        } else if (recipe.name.length > 100) {
            setNameError("Name is too long");
        } else {
            setNameError("");
            return true;
        }
        return false;
    };

    const isSourceUrlValid = () => {
        const urlPattern = /^(ftp|http|https):\/\/[^ "]+$/;

        if (recipe.sourceUrl.length === 0) {
            setSourceUrlError("URL is required");
        } else if (!urlPattern.test(recipe.sourceUrl)) {
            setSourceUrlError("Please enter a valid URL");
        } else {
            setSourceUrlError("");
            return true;
        }
        return false;
    };

    const isImageUrlValid = () => {
        const urlPattern = /^(ftp|http|https):\/\/[^ "]+$/;

        if (uploadedImage) {
            setImageError("");
            return true;
        }

        if (recipe.imageUrl.length === 0) {
            setImageError("Image is required");
        } else if (!urlPattern.test(recipe.imageUrl)) {
            setImageError("Please enter a valid URL");
        } else {
            setImageError("");
            return true;
        }
        return false;
    };

    const isLanagugeValid = () => {
        if (recipe.languageId === "0") {
            setLanguageError("Recipe language must be selected");
        } else {
            setLanguageError("");
            return true;
        }
        return false;
    };

    const isPreparationTimeValid = () => {
        if (recipe.preparationTime.length === 0) {
            setPreparationTimeError("Preparation time cannot be empty");
        } else if (recipe.preparationTime === "0") {
            setPreparationTimeError("Preparation time cannot be 0");
        } else if (recipe.preparationTime[0] === "-") {
            setPreparationTimeError("Preparation time cannot be negative");
        } else {
            setPreparationTimeError("");
            return true;
        }
        return false;
    };

    const areIngredientsValid = () => {
        if (recipe.ingredients.length < 3) {
            setIngredientsError("Ingredients must have at least 3 character");
        } else {
            setIngredientsError("");
            return true;
        }
        return false;
    };

    const isDescriptionValid = () => {
        if (recipe.description.length < 10) {
            setDescriptionError("Description must have at least 10 character");
        } else {
            setDescriptionError("");
            return true;
        }
        return false;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (
            !isNameValid() ||
            !isSourceUrlValid() ||
            !isImageUrlValid() ||
            !isPreparationTimeValid() ||
            !isLanagugeValid() ||
            !areIngredientsValid() ||
            !isDescriptionValid()
        ) {
            dispatch(setToast({ text: "One or more validation errors", isPositive: false }));
            return;
        }

        const bodyJson = {
            Id: recipe.id,
            UserId: recipe.userId,
            Name: recipe.name,
            SourceUrl: recipe.sourceUrl,
            ImageUrl: recipe.imageUrl,
            PreparationTime: recipe.preparationTime,
            IsPublic: recipe.isPublic,
            Ingredients: recipe.ingredients,
            Description: recipe.description,
            LanguageId: recipe.languageId,
            LanguageName: recipe.languageName,
            PortionCount: recipe.portionCount,
            Tags: selectedTags.map((item) => item.value).join(","),
        };

        const formData = new FormData();

        const jsonBlob = new Blob([JSON.stringify(bodyJson)], {
            type: "application/json",
        });
        formData.append("json", jsonBlob);

        if (uploadedImage) {
            formData.append("file", uploadedImage);
        }

        if (props.isEditMode) {
            await Api.putForm("editrecipe", formData).then((response) => {
                if (response.status === 200) {
                    dispatch(setToast({ text: "Recipe successfully edited", isPositive: true }));
                    navigate("/recipe/" + id);
                } else {
                    dispatch(setToast({ text: "Recipe not edited", isPositive: false }));
                }
            });
        } else {
            await Api.postForm("addrecipe", formData).then((response) => {
                if (response.status === 200) {
                    dispatch(setToast({ text: "Recipe successfully added", isPositive: true }));
                    navigate("/");
                } else {
                    dispatch(setToast({ text: "Recipe not added", isPositive: false }));
                }
            });
        }
    };

    const onAutoFill = () => {
        // TODO
        console.log("DEBUGX autofill clicked!");
    };

    useEffect(() => {
        isNameValid();
    }, [recipe.name]);

    useEffect(() => {
        isSourceUrlValid();
    }, [recipe.sourceUrl]);

    useEffect(() => {
        isImageUrlValid();
    }, [recipe.imageUrl, uploadedImage]);

    useEffect(() => {
        isPreparationTimeValid();
    }, [recipe.preparationTime]);

    useEffect(() => {
        areIngredientsValid();
    }, [recipe.ingredients]);

    useEffect(() => {
        isDescriptionValid();
    }, [recipe.description]);

    useEffect(() => {
        isLanagugeValid();
    }, [recipe.languageId]);

    useEffect(() => {
        if (isCopyrightConfirmed) {
            setRecipe({ ...recipe, sourceUrl: "https://recipins.wilcz.com" });
        }
    }, [isCopyrightConfirmed]);

    useEffect(() => {
        if (props.recipe != null && props.isEditMode === true) {
            setRecipe(props.recipe);
            setSelectedLanguage({
                value: props.recipe.languageId,
                label: props.recipe.languageName,
            });
        }
    }, [props.recipe]);

    useEffect(() => {
        setSelectStyle(
            languageError
                ? {
                      control: (provided: any) => ({
                          ...provided,
                          borderColor: "red",
                          minHeight: 58,
                          borderRadius: "6px",
                      }),
                  }
                : {
                      control: (provided: any) => ({
                          ...provided,
                          minHeight: 58,
                          borderRadius: "6px",
                      }),
                  },
        );
    }, [languageError]);

    useEffect(() => {
        if (props.isEditMode) {
            Api.get("tags/" + id)
                .then((res) => res.data)
                .then((result) => {
                    setSelectedTags(
                        result.map((tag: any) => ({
                            value: tag.id,
                            label: tag.name,
                        })),
                    );
                    setIsLoadingSelectedTags(false);
                });
        }
    }, [props.isEditMode]);

    useEffect(() => {
        Api.get("languages/all")
            .then((response) => {
                return response.data;
            })
            .then((result) => {
                setLangauges(
                    result.map((language: any) => ({
                        value: language.id,
                        label: language.name,
                    })),
                );
                setIsLoadingLanguages(false);
            });

        getOrUpdateTags();

        setNameError("");
        setPreparationTimeError("");
        setDescriptionError("");
        setIngredientsError("");
        setLanguageError("");
        setImageError("");
        setSourceUrlError("");
    }, []);

    return (
        <div className={isMobile ? styles.containerMobile : styles.containerDesktop}>
            <Form onSubmit={handleSubmit} className="my-3">
                <Form.Group controlId="recipeName" className="mb-3">
                    <FloatingLabel controlId="floatingName" label={t("components.feature.newEditRecipeForm.name")}>
                        <Form.Control
                            type="text"
                            name="name"
                            placeholder={t("components.feature.newEditRecipeForm.name")}
                            value={recipe.name}
                            onChange={handleChange}
                            isInvalid={!!nameError}
                            style={{ borderColor: nameError ? "red" : "" }}
                        />
                    </FloatingLabel>
                    <Form.Text style={{ color: "red" }}>{nameError}</Form.Text>
                </Form.Group>

                <Form.Group controlId="sourceUrl" className={`${styles.autoFillIconContainer} mb-3`}>
                    <div className="d-flex">
                        <div className={styles.greyLine}></div>
                        <div className={styles.rightContainer}>
                            <Form.Control
                                className={styles.formSpace}
                                type="text"
                                name="sourceUrl"
                                placeholder={t("components.feature.newEditRecipeForm.sourceUrl")}
                                value={recipe.sourceUrl}
                                onChange={handleChange}
                                isInvalid={!!sourceUrlError}
                                disabled={isCopyrightConfirmed}
                                style={{ borderColor: sourceUrlError ? "red" : "" }}
                            />
                            <div>{t("common.or")}</div>
                            <Form.Check
                                type="checkbox"
                                label={t("components.feature.newEditRecipeForm.copyrightConfirmation")}
                                onChange={onCopyrightConfirmed}
                            />
                        </div>
                    </div>
                    <Form.Text style={{ color: "red" }}>{sourceUrlError}</Form.Text>
                    {/*!props.isEditMode && 
            <div className={styles.autoFillIcon} onClick={() => onAutoFill()}>
              <i className="bi bi-stars"></i>
            </div> TODO: Hidden until add functionality */}
                </Form.Group>

                <Form.Group controlId="imageUrl" className="mb-3">
                    <div className="d-flex">
                        <div className={styles.greyLine}></div>
                        <div className={styles.middleContainer}>
                            <Form.Control
                                className={styles.formSpace}
                                type="text"
                                name="imageUrl"
                                placeholder={t("components.feature.newEditRecipeForm.imageUrl")}
                                value={recipe.imageUrl}
                                onChange={handleChange}
                                disabled={!!uploadedImage}
                                isInvalid={!!imageError}
                                style={{ borderColor: imageError ? "red" : "" }}
                            />
                            <div>{t("common.or")}</div>
                            <Form.Control
                                type="file"
                                disabled={!!recipe.imageUrl}
                                isInvalid={!!imageError}
                                onChange={onPhotoUpload}
                                accept="image/*"
                                style={{ borderColor: imageError ? "red" : "" }}
                            />
                        </div>
                        {(recipe.imageUrl || uploadedImage) && !imageError && (
                            <img
                                className={styles.image}
                                src={uploadedImage ? URL.createObjectURL(uploadedImage) : recipe.imageUrl}
                                onError={handleImageError}
                            ></img>
                        )}
                        {!((recipe.imageUrl || uploadedImage) && !imageError) && <div className={styles.image}></div>}
                    </div>
                    <Form.Text style={{ color: "red" }}>{imageError}</Form.Text>
                </Form.Group>

                <Form.Group controlId="preparationTime" className="mb-3">
                    <FloatingLabel
                        controlId="floatingPreparationTime"
                        label={t("components.feature.newEditRecipeForm.preparationTime")}
                    >
                        <Form.Control
                            type="number"
                            name="preparationTime"
                            placeholder={t("components.feature.newEditRecipeForm.preparationTime")}
                            min="0"
                            value={recipe.preparationTime}
                            onChange={handleChange}
                            isInvalid={!!preparationTimeError}
                            style={{ borderColor: preparationTimeError ? "red" : "" }}
                        />
                    </FloatingLabel>
                    <Form.Text style={{ color: "red" }}>{preparationTimeError}</Form.Text>
                </Form.Group>

                <Form.Group controlId="languageId" className="mb-3">
                    <Select
                        className={styles.langaugeForm}
                        isSearchable={true}
                        name="language"
                        isLoading={isLoadingLanguages}
                        value={selectedLanguage}
                        options={languages}
                        onChange={handleSelectChange}
                        styles={{ ...selectStyles, ...selectStyle }}
                        theme={selectTheme}
                    />
                    <Form.Text style={{ color: "red" }}>{languageError}</Form.Text>
                </Form.Group>

                <Form.Group controlId="isPublic" className="mb-3">
                    <ToggleButtonGroup
                        type="radio"
                        name="isPublic"
                        value={recipe.isPublic ? "true" : "false"}
                        onChange={(value) => handleIsPublicToggle(value === "true")}
                    >
                        <ToggleButton id="isPublic-true" variant="outline-dark" value="true">
                            <i className="bi bi-unlock-fill"></i> {t("components.feature.newEditRecipeForm.public")}
                        </ToggleButton>
                        <ToggleButton id="isPublic-false" variant="outline-dark" value="false">
                            {t("components.feature.newEditRecipeForm.private")} <i className="bi bi-lock-fill"></i>
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Form.Group>

                <Form.Group controlId="portionCount" className="mb-3">
                    <FloatingLabel
                        controlId="floatingPortionCount"
                        label={t("components.feature.newEditRecipeForm.portionCount")}
                    >
                        <Form.Control
                            type="number"
                            name="portionCount"
                            placeholder={t("components.feature.newEditRecipeForm.portionCount")}
                            min="0"
                            value={recipe.portionCount}
                            onChange={handleChange}
                        />
                    </FloatingLabel>
                </Form.Group>

                <Form.Group controlId="tags" className="mb-3">
                    <CreatableSelect
                        className={styles.tagsForm}
                        isSearchable={true}
                        isLoading={props.isEditMode ? isLoadingAllTags || isLoadingSelectedTags : isLoadingAllTags}
                        value={selectedTags}
                        isMulti
                        options={tags}
                        onChange={handleTagsChange}
                        onCreateOption={handleCreateTag}
                        styles={selectStyles}
                        theme={selectTheme}
                        placeholder={t("components.feature.newEditRecipeForm.tags")}
                        formatCreateLabel={(inputValue) =>
                            `${t("components.feature.newEditRecipeForm.createTag")}: \"${inputValue}\"`
                        }
                    />
                </Form.Group>

                <Form.Group controlId="ingredients" className="mb-3">
                    <FloatingLabel
                        controlId="floatingIngredients"
                        label={t("components.feature.newEditRecipeForm.ingredients")}
                    >
                        <Form.Control
                            as="textarea"
                            name="ingredients"
                            placeholder={t("components.feature.newEditRecipeForm.ingredients")}
                            value={recipe.ingredients}
                            onChange={handleChange}
                            isInvalid={!!ingredientsError}
                            style={{
                                height: "100px",
                                borderColor: ingredientsError ? "red" : "",
                            }}
                        />
                    </FloatingLabel>
                    <Form.Text style={{ color: "red" }}>{ingredientsError}</Form.Text>
                </Form.Group>

                <Form.Group controlId="description" className="mb-3">
                    <FloatingLabel
                        controlId="floatingDescription"
                        label={t("components.feature.newEditRecipeForm.directions")}
                    >
                        <Form.Control
                            as="textarea"
                            rows={4}
                            name="description"
                            placeholder={t("components.feature.newEditRecipeForm.directions")}
                            value={recipe.description}
                            onChange={handleChange}
                            isInvalid={!!descriptionError}
                            style={{
                                height: "200px",
                                borderColor: descriptionError ? "red" : "",
                            }}
                        />
                    </FloatingLabel>
                    <Form.Text style={{ color: "red" }}>{descriptionError}</Form.Text>
                </Form.Group>

                <Row className="justify-content-center">
                    <Col className="text-center">
                        <Button variant="dark" type="submit">
                            {props.isEditMode
                                ? t("components.feature.newEditRecipeForm.save")
                                : t("components.feature.newEditRecipeForm.add")}
                        </Button>
                    </Col>
                </Row>
            </Form>
        </div>
    );
};

export default NewEditRecipeForm;
